<template>
    <div class="d-inline-flex align-center">
        {{ groupName }}
        <v-btn @click="show" icon v-if="!disabled">
            <v-icon>mdi-chevron-down</v-icon>
        </v-btn>

        <base-dialog :loading="loading" :value="dialog" :width="460" :title="$t('common.select_group')"
            @input="val => { if (!val) hide() }" @close="hide">
            <kimsa-combobox :placeholder="$t('common.select_group')" dense :items="groups" @change="group = $event"
                :value="group" />

            <template v-slot:footer>
                <dialog-actions :confirm-label="$t('common.save')" :disabled="isDisabled" :loading="loading"
                    @cancel="hide" @confirm="checkAssignedRoutines" />
            </template>
        </base-dialog>

        <select-replace-assigned-routines-dialog :assigned-routines-in-same-date="assignedRoutinesInSameDate" mode="dates"
            persistent is-second-dialog @confirm="onConfirmReplace($event)" @cancel="onCancelReplace"
            ref="replaceRoutinesDates" />
    </div>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import {
    addAssignedRoutinesGroup,
    deleteAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getGroupAssignedRoutines,
    hasAssignedRoutineInDate,
    setStudentGroup
} from "@/utils/Exercises";
import SelectReplaceAssignedRoutinesDialog
    from "@/components/coach/routines/SelectReplaceAssignedRoutinesDialog";
export default {
    name: "SelectGroupStudentMenu",
    components: { SelectReplaceAssignedRoutinesDialog, KimsaCombobox, BaseDialog, DialogActions },
    props: {
        groups: {
            type: Array,
            default: () => [],
        },
        studentId: {
            type: String,
            default: null
        },
        studentName: {
            type: String,
            default: null
        },
        currentGroup: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            group: null,
            groupRoutines: [],
            assignedRoutinesInSameDate: [],
        }
    },
    computed: {
        groupName(vm = this) {
            let findGroup = vm.groups.find(group => group.id === vm.currentGroup)
            return findGroup?.name || vm.$t('common.without_group')
        },
        isDisabled(vm = this) {
            return !vm.group
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.group = null
            vm.groupRoutines = []
            vm.assignedRoutinesInSameDate = []
            vm.setDefaults()
        },
        async checkAssignedRoutines() {
            const vm = this
            vm.loading = true

            vm.groupRoutines = await getGroupAssignedRoutines(vm.group.id)

            if (vm.groupRoutines.length === 0) return vm.save()

            const checks = await Promise.all(vm.groupRoutines.map(routine =>
                hasAssignedRoutineInDate(vm.studentId, routine.rawDate.full, routine.routineNumber || 1)
            ))
            const assignedRoutinesInSameDate = checks
                .map((res, i) => res.has ? {
                    assignedRoutineId: res.id,
                    date: vm.$moment(vm.groupRoutines[i].rawDate.full).format('DD-MM-YYYY'),
                    replace: false,
                } : null)
                .filter(Boolean)

            if (assignedRoutinesInSameDate.length > 0) {
                vm.assignedRoutinesInSameDate = assignedRoutinesInSameDate
                vm.$refs.replaceRoutinesDates.show()
            } else {
                await Promise.all(vm.groupRoutines.map(async routine => {
                    await deleteAssignedRoutineStudentInDate(vm.studentId, routine.rawDate.full, routine.routineNumber || 1)
                    await addAssignedRoutinesGroup({ ...routine, date: routine.rawDate.full }, [vm.studentId])
                }))
                await vm.save()
            }
        },
        async onConfirmReplace({ toReplace }) {
            const vm = this
            if (toReplace.length > 0) {
                const toDelete = toReplace.filter(ar => ar.replace).map(ar => ar.assignedRoutineId)
                if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)

                const skipRoutinesDates = toReplace.filter(ar => !ar.replace).map(ar => ar.date)
                const toAdd = vm.groupRoutines.filter(routine =>
                    !skipRoutinesDates.includes(vm.$moment(routine.rawDate.full).format('DD-MM-YYYY'))
                )
                await Promise.all(toAdd.map(async routine => {
                    await deleteAssignedRoutineStudentInDate(vm.studentId, routine.rawDate.full, routine.routineNumber || 1)
                    await addAssignedRoutinesGroup({ ...routine, date: routine.rawDate.full }, [vm.studentId])
                }))
            }
            await vm.save()
        },
        onCancelReplace() {
            this.loading = false
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                await setStudentGroup(vm.studentId, vm.group.id, vm.currentGroup || null)

                vm.$emit('save', vm.studentId)
                vm.hide()
            })
        },
        setDefaults() {
            const vm = this
            if (!vm.currentGroup) return
            let findGroup = vm.groups.find(group => group.id === vm.currentGroup)
            if (!findGroup) return
            vm.group = { id: findGroup.id, ...findGroup }
        }
    },
    watch: {
        currentGroup() { this.setDefaults() },
    }
}
</script>
